import { useMemo, useState, useEffect, useCallback } from 'react'
import { useLocation, useHistory } from 'react-router-dom'

import { QUERY_SOURCE_TYPE } from 'components/agent_hub_components'
import { AI_APP_PATH } from 'features/agent_hub/ai_chat_discovery/constants'
import { AISearchPage } from 'features/agent_hub/aiSearchPage/AISearchPage'
import { ChatPage } from 'features/agent_hub/chatPage/ChatPage'
import { useGraphQLChat, useThreadMessages } from 'features/agent_hub/hooks'
import { createAnalyticsHandlers } from 'features/agent_hub/utils/analyticsHandlers'
import { useWideCenterPanelLayout } from 'features/app_shell'
import { useHeaderBorderBottom } from 'features/layout'
import { useRightRail } from 'features/right_rail'
import { useTrack } from 'hooks/analytics_events'
import { isValidUUID } from 'shared/utils/uuid'

/**
 * Full-page `/app/ai` owns wide main layout and opts into a header border bottom.
 * Embedded rail chat must not call these hooks — they use layout effects with no
 * dependency list and would run after the host page, overwriting values such as
 * `useWideCenterPanelLayout` on scheduled events.
 *
 * The header border is needed because this page renders no page header (PageHeaderRenderer
 * is not used), so there is no natural visual separator between the global header and the
 * page content. `useHeaderBorderBottom` adds that separator via the global header's border.
 */
const FullPageMainLayoutEffects = () => {
  useWideCenterPanelLayout()
  useHeaderBorderBottom(true)
  return null
}

const ChatPageWrapper = ({
  threadId,
  initialQuery,
  initialQuerySourceType,
  analyticsHandlers,
  embedded,
  onNewChatPressed,
  graphQlOptions,
}) => {
  const { messages: threadMessages, isLoading: isLoadingThread } = useThreadMessages(threadId)
  const { messages, handleSendMessage, isLoading, onCancelGeneration, toolCalls } = useGraphQLChat(
    initialQuery,
    initialQuerySourceType,
    analyticsHandlers,
    threadMessages,
    threadId,
    graphQlOptions
  )

  const handleSendMessageWithAnalytics = (message, querySourceType) => {
    analyticsHandlers.trackChatSearchSubmitted()
    handleSendMessage(message, querySourceType)
  }

  return (
    <ChatPage
      embedded={embedded}
      messages={messages}
      onSendMessage={handleSendMessageWithAnalytics}
      isLoading={isLoading || isLoadingThread}
      onCancelGeneration={onCancelGeneration}
      analyticsHandlers={analyticsHandlers}
      onNewChatPressed={onNewChatPressed}
      toolCalls={toolCalls}
    />
  )
}


export const ChatApp = ({ embedded = false, threadId: threadIdFromPane }) => {
  const location = useLocation()
  const history = useHistory()
  const { openOrReplace } = useRightRail()
  const track = useTrack()
  const [searchState, setSearchState] = useState(null)

  const analyticsHandlers = useMemo(() => createAnalyticsHandlers(track), [track])

  const urlParams = new URLSearchParams(location.search)
  const threadIdParam = urlParams.get('threadId')

  const paneThreadId = embedded && isValidUUID(threadIdFromPane) ? threadIdFromPane : null
  const urlThreadId = !embedded && isValidUUID(threadIdParam) ? threadIdParam : null
  const activeThreadId = embedded ? paneThreadId : urlThreadId

  const hasValidThreadId = !!activeThreadId

  const graphQlOptions = useMemo(() => {
    if (!embedded) {
      return {}
    }
    return {
      onCommitThreadId: (tid) => openOrReplace('ai_chat', { threadId: tid }),
    }
  }, [embedded, openOrReplace])

  const handleNewChatPressed = useCallback(() => {
    analyticsHandlers.trackNavigationLinkInChatClicked('new_chat')
    setSearchState(null)
    history.push(AI_APP_PATH)
  }, [analyticsHandlers, history])

  /** Full-page `/app/ai` only: drop invalid `threadId` query values. Embedded rail ignores URL threadId. */
  useEffect(() => {
    if (embedded) return
    if (!threadIdParam || isValidUUID(threadIdParam)) return
    history.replace(AI_APP_PATH)
  }, [threadIdParam, history, embedded])

  useEffect(() => {
    if (!hasValidThreadId) {
      setSearchState(null)
    }
  }, [hasValidThreadId])

  if (hasValidThreadId) {
    return (
      <>
        {!embedded && <FullPageMainLayoutEffects />}
        <ChatPageWrapper
          threadId={activeThreadId}
          analyticsHandlers={analyticsHandlers}
          embedded={embedded}
          onNewChatPressed={handleNewChatPressed}
          graphQlOptions={graphQlOptions}
        />
      </>
    )
  }

  if (searchState) {
    return (
      <>
        {!embedded && <FullPageMainLayoutEffects />}
        <ChatPageWrapper
          initialQuery={searchState.query}
          initialQuerySourceType={searchState.querySourceType}
          analyticsHandlers={analyticsHandlers}
          embedded={embedded}
          onNewChatPressed={handleNewChatPressed}
          graphQlOptions={graphQlOptions}
        />
      </>
    )
  }

  const handleSearch = (query, querySourceType = QUERY_SOURCE_TYPE.TEXT) => {
    setSearchState({ query, querySourceType })
  }

  return (
    <>
      {!embedded && <FullPageMainLayoutEffects />}
      <AISearchPage
        handleSearch={handleSearch}
        analyticsHandlers={analyticsHandlers}
        embedded={embedded}
      />
    </>
  )
}
